import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '@/js/firebase'
import { logger } from '@/services/loggerService'
import { storageSubscriptionService, isPlatformSubscription } from '@/services/storageSubscriptionService'

/**
 * Service for tracking and managing storage usage
 * Uses a hybrid approach: increment/decrement on upload/delete + daily sync
 */
class StorageUsageService {
  constructor() {
    this.companyCollection = 'companies'
    this.contractCollection = 'contracts'
  }

  /**
   * Get current storage usage for a company
   * Returns the cached snapshot from company document
   */
  async getUsage(companyId) {
    try {
      const companyRef = doc(db, this.companyCollection, companyId)
      const companySnap = await getDoc(companyRef)

      if (!companySnap.exists()) {
        logger.warn('[StorageUsageService] Company not found', { companyId })
        return null
      }

      const data = companySnap.data()
      const snapshot = data.storageSnapshot || {
        currentBytes: 0,
        limitBytes: 0,
        percentUsed: 0,
        tier: 'basic'
      }

      logger.debug('[StorageUsageService] Retrieved usage snapshot', {
        companyId,
        currentBytes: snapshot.currentBytes,
        percentUsed: snapshot.percentUsed
      })

      return snapshot
    } catch (error) {
      logger.error('[StorageUsageService] Failed to get usage', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Increment storage usage after asset upload
   * Updates both company snapshot and subscription stats
   */
  async incrementUsage(companyId, bytes) {
    try {
      if (!bytes || bytes <= 0) {
        logger.debug('[StorageUsageService] No bytes to increment', { companyId, bytes })
        return
      }

      logger.info('[StorageUsageService] Incrementing usage', { companyId, bytes })

      // Get current subscription to check limits
      const subscription = await storageSubscriptionService.getSubscription(companyId)
      const effectiveLimit = subscription
        ? storageSubscriptionService.calculateEffectiveLimit(subscription)
        : 0

      // Update company snapshot
      const companyRef = doc(db, this.companyCollection, companyId)
      const companySnap = await getDoc(companyRef)

      if (!companySnap.exists()) {
        throw new Error('Company not found')
      }

      const currentSnapshot = companySnap.data().storageSnapshot || { currentBytes: 0 }
      const newBytes = (currentSnapshot.currentBytes || 0) + bytes
      const newPercentUsed = effectiveLimit > 0 ? (newBytes / effectiveLimit) * 100 : 0

      await updateDoc(companyRef, {
        'storageSnapshot.currentBytes': newBytes,
        'storageSnapshot.percentUsed': newPercentUsed,
        'storageSnapshot.lastUpdatedAt': serverTimestamp()
      })

      // Update subscription usage stats if exists
      if (subscription) {
        const subscriptionRef = doc(db, this.contractCollection, subscription.id)
        await updateDoc(subscriptionRef, {
          'usageStats.currentBytes': newBytes,
          'usageStats.percentUsed': newPercentUsed,
          'usageStats.lastUpdatedAt': serverTimestamp()
        })

        // Check if approaching or exceeding limit
        await this.checkLimitStatus(companyId, newBytes, effectiveLimit, subscription)
      }

      logger.info('[StorageUsageService] Usage incremented', {
        companyId,
        addedBytes: bytes,
        newTotalBytes: newBytes,
        percentUsed: newPercentUsed.toFixed(2)
      })

      return {
        currentBytes: newBytes,
        percentUsed: newPercentUsed,
        limitBytes: effectiveLimit
      }
    } catch (error) {
      logger.error('[StorageUsageService] Failed to increment usage', {
        error: error.message,
        companyId,
        bytes
      })
      throw error
    }
  }

  /**
   * Decrement storage usage after asset deletion
   */
  async decrementUsage(companyId, bytes) {
    try {
      if (!bytes || bytes <= 0) {
        logger.debug('[StorageUsageService] No bytes to decrement', { companyId, bytes })
        return
      }

      logger.info('[StorageUsageService] Decrementing usage', { companyId, bytes })

      // Get current subscription
      const subscription = await storageSubscriptionService.getSubscription(companyId)
      const effectiveLimit = subscription
        ? storageSubscriptionService.calculateEffectiveLimit(subscription)
        : 0

      // Update company snapshot
      const companyRef = doc(db, this.companyCollection, companyId)
      const companySnap = await getDoc(companyRef)

      if (!companySnap.exists()) {
        throw new Error('Company not found')
      }

      const currentSnapshot = companySnap.data().storageSnapshot || { currentBytes: 0 }
      const newBytes = Math.max(0, (currentSnapshot.currentBytes || 0) - bytes)
      const newPercentUsed = effectiveLimit > 0 ? (newBytes / effectiveLimit) * 100 : 0

      await updateDoc(companyRef, {
        'storageSnapshot.currentBytes': newBytes,
        'storageSnapshot.percentUsed': newPercentUsed,
        'storageSnapshot.lastUpdatedAt': serverTimestamp()
      })

      // Update subscription usage stats if exists.
      // Note: status + gracePeriod transitions are owned server-side by
      // evaluateStorageState (fires via the onAssetDeleted Firestore trigger
      // -> syncCompanyUsage). This client write is just an optimistic
      // current-bytes update so the UI doesn't lag the round-trip.
      if (subscription) {
        const subscriptionRef = doc(db, this.contractCollection, subscription.id)
        await updateDoc(subscriptionRef, {
          'usageStats.currentBytes': newBytes,
          'usageStats.percentUsed': newPercentUsed,
          'usageStats.lastUpdatedAt': serverTimestamp()
        })
      }

      logger.info('[StorageUsageService] Usage decremented', {
        companyId,
        removedBytes: bytes,
        newTotalBytes: newBytes,
        percentUsed: newPercentUsed.toFixed(2)
      })

      return {
        currentBytes: newBytes,
        percentUsed: newPercentUsed,
        limitBytes: effectiveLimit
      }
    } catch (error) {
      logger.error('[StorageUsageService] Failed to decrement usage', {
        error: error.message,
        companyId,
        bytes
      })
      throw error
    }
  }

  /**
   * Set absolute usage value (used by sync function)
   */
  async setUsage(companyId, bytes) {
    try {
      logger.info('[StorageUsageService] Setting absolute usage', { companyId, bytes })

      // Get current subscription
      const subscription = await storageSubscriptionService.getSubscription(companyId)
      const effectiveLimit = subscription
        ? storageSubscriptionService.calculateEffectiveLimit(subscription)
        : 0

      const percentUsed = effectiveLimit > 0 ? (bytes / effectiveLimit) * 100 : 0

      // Update company snapshot
      const companyRef = doc(db, this.companyCollection, companyId)
      await updateDoc(companyRef, {
        'storageSnapshot.currentBytes': bytes,
        'storageSnapshot.percentUsed': percentUsed,
        'storageSnapshot.lastUpdatedAt': serverTimestamp()
      })

      // Update subscription usage stats if exists
      if (subscription) {
        const subscriptionRef = doc(db, this.contractCollection, subscription.id)
        await updateDoc(subscriptionRef, {
          'usageStats.currentBytes': bytes,
          'usageStats.percentUsed': percentUsed,
          'usageStats.lastSyncedAt': serverTimestamp(),
          'usageStats.lastUpdatedAt': serverTimestamp()
        })

        // Check limit status
        await this.checkLimitStatus(companyId, bytes, effectiveLimit, subscription)
      }

      logger.info('[StorageUsageService] Usage set', {
        companyId,
        bytes,
        percentUsed: percentUsed.toFixed(2)
      })

      return {
        currentBytes: bytes,
        percentUsed,
        limitBytes: effectiveLimit
      }
    } catch (error) {
      logger.error('[StorageUsageService] Failed to set usage', {
        error: error.message,
        companyId,
        bytes
      })
      throw error
    }
  }

  /**
   * Check if usage is approaching or exceeding limit
   * Triggers notifications and grace periods as needed
   */
  async checkLimitStatus(companyId, currentBytes, effectiveLimit, subscription) {
    try {
      // Platform accounts are metered, never blocked — no client-side overage
      // grace. Billing-required grace is started server-side (usageSync).
      if (isPlatformSubscription(subscription)) {
        const percentUsed = effectiveLimit > 0 ? (currentBytes / effectiveLimit) * 100 : 0
        return { percentUsed, status: storageSubscriptionService.getUsageStatusFor(subscription, percentUsed) }
      }

      const percentUsed = effectiveLimit > 0 ? (currentBytes / effectiveLimit) * 100 : 0

      // Warning thresholds
      const WARNING_THRESHOLD = 80 // 80%
      const CRITICAL_THRESHOLD = 95 // 95%
      const OVERAGE_THRESHOLD = 100 // 100%

      if (percentUsed >= OVERAGE_THRESHOLD) {
        // Over limit - start overage grace period if not already in one
        if (!subscription.gracePeriod) {
          logger.warn('[StorageUsageService] Storage limit exceeded', {
            companyId,
            currentBytes,
            effectiveLimit,
            percentUsed
          })

          await storageSubscriptionService.startOverageGracePeriod(subscription.id)
          // Note: Threshold email notifications are sent by the scheduled usageSync Cloud Function
          // which runs daily and sends to the company's primary contact
        }
      } else if (percentUsed >= CRITICAL_THRESHOLD) {
        logger.warn('[StorageUsageService] Storage critically low', {
          companyId,
          percentUsed
        })
        // Note: Threshold email notifications are handled by the scheduled usageSync Cloud Function
      } else if (percentUsed >= WARNING_THRESHOLD) {
        logger.info('[StorageUsageService] Storage approaching limit', {
          companyId,
          percentUsed
        })
        // Note: Threshold email notifications are handled by the scheduled usageSync Cloud Function
      }

      return { percentUsed, status: this.getUsageStatus(percentUsed) }
    } catch (error) {
      logger.error('[StorageUsageService] Failed to check limit status', {
        error: error.message,
        companyId
      })
      // Don't throw - limit checking is non-critical
      return { percentUsed: 0, status: 'unknown' }
    }
  }

  /**
   * Get usage status based on percentage
   */
  getUsageStatus(percentUsed) {
    if (percentUsed >= 100) return 'overage'
    if (percentUsed >= 95) return 'critical'
    if (percentUsed >= 80) return 'warning'
    return 'normal'
  }

  /**
   * Check if upload would exceed storage limit
   * Returns true if upload should be blocked
   */
  async wouldExceedLimit(companyId, uploadBytes) {
    try {
      const subscription = await storageSubscriptionService.getSubscription(companyId)

      if (!subscription) {
        // No subscription - allow upload (will create basic tier)
        return false
      }

      const effectiveLimit = storageSubscriptionService.calculateEffectiveLimit(subscription)
      const currentBytes = subscription.usageStats?.currentBytes || 0

      // Allow up to 7-day grace period overage
      // Block only if they're already in overage grace and trying to upload more
      if (subscription.gracePeriod?.type === 'overage') {
        logger.warn('[StorageUsageService] Upload blocked - in overage grace period', {
          companyId,
          currentBytes,
          uploadBytes,
          effectiveLimit
        })
        return true
      }

      // Allow the upload even if it goes over (triggers grace period)
      return false
    } catch (error) {
      logger.error('[StorageUsageService] Failed to check upload limit', {
        error: error.message,
        companyId,
        uploadBytes
      })
      // On error, allow upload to avoid blocking users
      return false
    }
  }

  /**
   * Get detailed usage breakdown
   * Returns usage by asset type, recent uploads, etc.
   */
  async getUsageBreakdown(companyId) {
    try {
      await this.getUsage(companyId) // Ensure cache is fresh
      const subscription = await storageSubscriptionService.getSubscription(companyId)

      if (!subscription) {
        return {
          currentBytes: 0,
          limitBytes: 0,
          percentUsed: 0,
          tier: 'basic',
          addOns: [],
          effectiveLimit: 0,
          status: 'normal',
          gracePeriod: null
        }
      }

      // Derive the limit from the live tier config (mirrors server usageSync) so a
      // subscription with a stale frozen tierLimits still reflects its current tier.
      const config = await storageSubscriptionService.loadTierConfig()
      const effectiveLimit = storageSubscriptionService.calculateEffectiveLimit(subscription, config?.tiers)
      // Platform: the tier's storageBytes is the uvp/podcast allowance, not this
      // account's — report the resolved allowance instead.
      const tierBytes = isPlatformSubscription(subscription)
        ? effectiveLimit
        : config?.tiers?.[subscription.tier]?.storageBytes
          ?? subscription.tierLimits?.storageBytes
          ?? 0
      const currentBytes = subscription.usageStats?.currentBytes || 0
      const percentUsed = effectiveLimit > 0 ? (currentBytes / effectiveLimit) * 100 : 0

      return {
        currentBytes,
        limitBytes: tierBytes,
        percentUsed,
        tier: subscription.tier,
        tierName: await this.getTierDisplayName(subscription.tier),
        addOns: subscription.addOns || [],
        addonBytes: (subscription.addOns || []).reduce((sum, a) => sum + a.sizeBytes, 0),
        effectiveLimit,
        status: storageSubscriptionService.getUsageStatusFor(subscription, percentUsed),
        gracePeriod: subscription.gracePeriod,
        qualifiedBy: subscription.qualifiedBy,
        lastSyncedAt: subscription.usageStats?.lastSyncedAt,
        lastUpdatedAt: subscription.usageStats?.lastUpdatedAt
      }
    } catch (error) {
      logger.error('[StorageUsageService] Failed to get usage breakdown', {
        error: error.message,
        companyId
      })
      throw error
    }
  }

  /**
   * Get display name for tier
   */
  async getTierDisplayName(tier) {
    try {
      const config = await storageSubscriptionService.loadTierConfig()
      return config.tiers?.[tier]?.name || tier
    } catch {
      return tier
    }
  }

  /**
   * Format bytes for display
   */
  formatBytes(bytes) {
    return storageSubscriptionService.formatBytes(bytes)
  }

}

export const storageUsageService = new StorageUsageService()
